// src/communaute/api.js
//
// Appels à l'API Laravel Thrundrz — mêmes routes que l'app Flutter.
// ⚙️ À ADAPTER si besoin :
//   - API_URL  : via VITE_API_URL dans .env, sinon l'URL de prod
//   - getToken : clé du localStorage où ton dashboard stocke le JWT

import { base_url } from '../utils/constante';
import {
  normComment,
  normConversation,
  normMessage,
  normNotif,
  normPost,
  normUser,
} from './normalize';

const API_URL = base_url
export function getToken() {
  try {
    return localStorage.getItem('token');
  } catch {
    return null;
  }
}

/** Id de l'utilisateur connecté (claim "sub" du JWT tymon), 0 si inconnu */
export function monId() {
  const t = getToken();
  if (!t) return 0;
  try {
    let p = t.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    while (p.length % 4) p += '=';
    const json = JSON.parse(atob(p));
    return Number.parseInt(json.sub, 10) || 0;
  } catch {
    return 0;
  }
}

// ══════════════════════════════════════════════════════
//  REQUÊTE GÉNÉRIQUE
// ══════════════════════════════════════════════════════
async function requete(method, chemin, { body, form, params } = {}) {
  const url = new URL(chemin, API_URL);
  if (params) {
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, v);
    });
  }

  const headers = { Accept: 'application/json' };
  const t = getToken();
  if (t) headers.Authorization = `Bearer ${t}`;

  let payload;
  if (form) {
    payload = form; // FormData : le navigateur met le Content-Type
  } else if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }

  try {
    const res = await fetch(url, { method, headers, body: payload });
    let data = {};
    try {
      data = (await res.json()) ?? {};
    } catch {
      /* réponse vide ou HTML */
    }
    if (!res.ok) {
      console.warn(`[COMMUNAUTE] ${method} ${url.pathname} → ${res.status}`, data);
    }
    return { ok: res.ok, status: res.status, data };
  } catch (e) {
    console.error(`[COMMUNAUTE] ${method} ${url.pathname} → réseau`, e);
    return { ok: false, status: 0, data: {} };
  }
}

const ok = (r) => r.ok && r.data?.success !== false;

function pagine(r, norm) {
  const d = r.data || {};
  return {
    items: (Array.isArray(d.data) ? d.data : []).map(norm),
    page: d.current_page || 1,
    lastPage: d.last_page || 1,
    total: d.total || 0,
    nonLues: d.non_lues || 0,
  };
}

// ══════════════════════════════════════════════════════
//  PUBLICATIONS
// ══════════════════════════════════════════════════════
export async function getPosts(page = 1, q = '') {
  return pagine(await requete('GET', 'posts', { params: { page, q } }), normPost);
}

export async function getMesPosts(page = 1) {
  return pagine(await requete('GET', 'posts/mine', { params: { page } }), normPost);
}

export async function getPost(id) {
  const r = await requete('GET', `posts/${id}`);
  return ok(r) && r.data.data ? normPost(r.data.data) : null;
}

/** champs : { titre, contenu, lien_youtube, lien_facebook, lien_tiktok, lien_instagram } */
export async function creerPost(champs, images = []) {
  const form = new FormData();
  Object.entries(champs).forEach(([k, v]) => {
    if (v !== undefined && v !== null && `${v}`.trim() !== '') form.append(k, `${v}`.trim());
  });
  images.forEach((f) => form.append('images[]', f));

  const r = await requete('POST', 'posts', { form });
  if (ok(r) && r.data.data) return { post: normPost(r.data.data) };

  // 422 : on remonte le premier message de validation
  const erreurs = r.data?.errors;
  const premier = erreurs ? Object.values(erreurs).flat()[0] : null;
  return { erreur: premier || r.data?.message || 'Échec de la publication' };
}

export async function supprimerPost(id) {
  return ok(await requete('DELETE', `posts/${id}`));
}

export async function toggleLike(id) {
  const r = await requete('POST', `posts/${id}/like`);
  if (!ok(r)) return null;
  return {
    isLiked: r.data.is_liked === true || r.data.is_liked === 1,
    nbrLikes: Number.parseInt(r.data.nbr_likes ?? 0, 10) || 0,
  };
}

export async function toggleFavori(id) {
  const r = await requete('POST', `posts/${id}/favory`);
  return ok(r) ? r.data.is_favory === true || r.data.is_favory === 1 : null;
}

export async function signalerPost(id, motif) {
  return ok(await requete('POST', `posts/${id}/report`, { body: { motif } }));
}

export async function toggleFollow(userId) {
  const r = await requete('POST', `users/${userId}/follow`);
  return ok(r) ? r.data.is_followed === true || r.data.is_followed === 1 : null;
}

// ══════════════════════════════════════════════════════
//  COMMENTAIRES
// ══════════════════════════════════════════════════════
export async function getComments(postId, page = 1) {
  return pagine(await requete('GET', `posts/${postId}/comments`, { params: { page } }), normComment);
}

export async function getReplies(commentId, page = 1) {
  return pagine(await requete('GET', `comments/${commentId}/replies`, { params: { page } }), normComment);
}

export async function ajouterComment(postId, contenu, parentId = null) {
  const r = await requete('POST', `posts/${postId}/comments`, {
    body: { contenu, ...(parentId ? { parent_id: parentId } : {}) },
  });
  return ok(r) && r.data.data ? normComment(r.data.data) : null;
}

export async function supprimerComment(id) {
  return ok(await requete('DELETE', `comments/${id}`));
}

// ══════════════════════════════════════════════════════
//  MESSAGERIE
// ══════════════════════════════════════════════════════
/** filtre : tous | prive | groupe | archives */
export async function getConversations(filtre = 'tous', page = 1) {
  const r = await requete('GET', 'conversations', { params: { page, filtre } });
  return ok(r) ? (r.data.data || []).map(normConversation) : [];
}

export async function getAmis(q = '') {
  const r = await requete('GET', 'conversations/amis', { params: { q: q.trim() } });
  return ok(r) ? (r.data.data || []).map(normUser) : [];
}

export async function ouvrirPrive(userId) {
  const r = await requete('POST', 'conversations/prive', { body: { user_id: userId } });
  return ok(r) && r.data.data ? normConversation(r.data.data) : null;
}

export async function getConversationDetails(id) {
  const r = await requete('GET', `conversations/${id}/details`);
  return ok(r) && r.data.data ? normConversation(r.data.data) : null;
}

export async function creerGroupe(nom, userIds, photo = null) {
  const form = new FormData();
  form.append('nom', nom);
  userIds.forEach((id, i) => form.append(`user_ids[${i}]`, id));
  if (photo) form.append('photo', photo);
  const r = await requete('POST', 'conversations/groupe', { form });
  return ok(r) && r.data.data ? normConversation(r.data.data) : null;
}

export async function modifierGroupe(id, { nom, photo, supprimerPhoto } = {}) {
  const form = new FormData();
  if (nom?.trim()) form.append('nom', nom.trim());
  if (supprimerPhoto) form.append('supprimer_photo', '1');
  else if (photo) form.append('photo', photo);
  return ok(await requete('POST', `conversations/${id}`, { form }));
}

export async function ajouterMembres(id, userIds) {
  const r = await requete('POST', `conversations/${id}/membres`, { body: { user_ids: userIds } });
  return { succes: ok(r), message: r.data?.message || '' };
}

export async function retirerMembre(id, userId) {
  return ok(await requete('DELETE', `conversations/${id}/membres/${userId}`));
}

export async function changerRole(id, userId, role) {
  return ok(await requete('POST', `conversations/${id}/membres/${userId}/role`, { body: { role } }));
}

export async function archiverConversation(id, archiver = true) {
  return ok(await requete('POST', `conversations/${id}/archiver`, { body: { archiver } }));
}

export async function basculerMuet(id, muet = true) {
  return ok(await requete('POST', `conversations/${id}/muet`, { body: { muet } }));
}

export async function quitterConversation(id) {
  return ok(await requete('DELETE', `conversations/${id}`));
}

export async function getMessages(id, page = 1) {
  const r = await requete('GET', `conversations/${id}/messages`, { params: { page } });
  return pagine(ok(r) ? r : { data: {} }, normMessage);
}

export async function getNouveauxMessages(id, afterId) {
  const r = await requete('GET', `conversations/${id}/messages`, { params: { after: afterId } });
  return ok(r) ? (r.data.data || []).map(normMessage) : [];
}

export async function envoyerMessage(id, contenu) {
  const r = await requete('POST', `conversations/${id}/messages`, { body: { type: 'texte', contenu } });
  return ok(r) && r.data.data ? normMessage(r.data.data) : null;
}

export async function supprimerMessage(messageId) {
  return ok(await requete('DELETE', `conversations/messages/${messageId}`));
}

export async function marquerConversationLue(id) {
  await requete('POST', `conversations/${id}/lu`);
}

export async function totalMessagesNonLus() {
  const r = await requete('GET', 'conversations/non-lus');
  return r.ok ? Number.parseInt(r.data.total ?? 0, 10) || 0 : 0;
}

export async function bloquerUtilisateur(userId) {
  return ok(await requete('POST', `users/${userId}/bloquer`, { body: {} }));
}

// ══════════════════════════════════════════════════════
//  NOTIFICATIONS
// ══════════════════════════════════════════════════════
/** filtre : tous | non_lues */
export async function getNotifications(page = 1, filtre = 'tous') {
  return pagine(await requete('GET', 'notifications', { params: { page, filtre } }), normNotif);
}

export async function nbrNotifsNonLues() {
  const r = await requete('GET', 'notifications/non-lues');
  return r.ok ? Number.parseInt(r.data.total ?? 0, 10) || 0 : 0;
}

export async function marquerNotifLue(id) {
  return (await requete('POST', `notifications/${id}/lu`)).ok;
}

export async function toutMarquerLu() {
  return (await requete('POST', 'notifications/tout-lu')).ok;
}

export async function supprimerNotif(id) {
  return (await requete('DELETE', `notifications/${id}`)).ok;
}

export async function toutSupprimerNotifs() {
  return (await requete('DELETE', 'notifications')).ok;
}